// (C) Copyright 2015 Moodle Pty Ltd.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { Injectable } from '@angular/core';
import { CoreSiteWSPreSets } from '@classes/sites/authenticated-site';
import { CoreSite } from '@classes/sites/site';
import { CoreSites, CoreSitesCommonWSOptions } from '@services/sites';
import { makeSingleton } from '@singletons';
import { WorkplaceCatalogueApplyFilters } from '../components/filter/filter';

/**
 * Service that provides some features to manage course catalogue.
 */
@Injectable({ providedIn: 'root' })
export class WorkplaceCatalogueService {

    static readonly ROOT_CACHE_KEY = 'WorkplaceCatalogue:';

    /**
     * Retrieves catalogue page.
     *
     * @param query Query.
     * @param filters Additional filters.
     * @param returnFilters Whether to return available filters.
     * @param options WS options.
     * @returns Promise resolved when the info is retrieved.
     */
    async getCataloguePage(
        query: WorkplaceToolCatalogueGetCatalogueQuery,
        filters: WorkplaceCatalogueApplyFilters = {},
        returnFilters = false,
        options?: CoreSitesCommonWSOptions,
    ): Promise<WorkplaceToolCatalogueGetCataloguePageWSResponse> {
        const site = await CoreSites.getSite(options?.siteId);

        const queryParts: string[] = [];
        for(const fieldname in filters) {
            let arrayCount = 0;
            for(const value in filters[fieldname]) {
                if (filters[fieldname][value]) {
                    queryParts.push(`${fieldname}[${arrayCount}]=${value}`);
                    arrayCount++;
                }
            }
        }

        const queryFilters = queryParts.join('&');

        const params: WorkplaceToolCatalogueGetCataloguePageWSParams = {
            query: this.getCataloguePageQuery(query, queryFilters),
            returnfilters: returnFilters,
            isfrontpage: !returnFilters,
        };

        const preSets: CoreSiteWSPreSets = {
            cacheKey: this.getCataloguePageCacheKey(query, queryFilters),
            updateFrequency: CoreSite.FREQUENCY_SOMETIMES,
            ...CoreSites.getReadingStrategyPreSets(options?.readingStrategy),
        };

        return await site.read<WorkplaceToolCatalogueGetCataloguePageWSResponse>(
            'tool_catalogue_get_catalogue_page',
            params,
            preSets,
        );
    }

    /**
     * Check if tool_catalogue_get_catalogue_page WS is available.
     *
     * @param siteId Site ID. If not defined, use current site.
     * @returns Whether the webservice is available.
     * @since 4.5
     */
    async isGetCataloguePageAvailable(siteId?: string): Promise<boolean> {
        const site = await CoreSites.getSite(siteId);

        return site?.wsAvailable('tool_catalogue_get_catalogue_page');
    }

    /**
     * Get catalogue featured courses
     *
     * @param page Page to get.
     * @param options WS options.
     * @returns Featured courses list.
     */
    async getCatalogueFeaturedCourses(
        page = 0,
        options?: CoreSitesCommonWSOptions,
    ): Promise<WorkplaceCatalogueGetCatalogueFeaturedCoursesWSResponse> {
        const site = await CoreSites.getSite(options?.siteId);

        const preSets: CoreSiteWSPreSets = {
            cacheKey: this.getFeaturedCoursesCacheKey(page),
            updateFrequency: CoreSite.FREQUENCY_SOMETIMES,
            ...CoreSites.getReadingStrategyPreSets(options?.readingStrategy),
        };

        return await site.read<WorkplaceCatalogueGetCatalogueFeaturedCoursesWSResponse>(
            'tool_catalogue_get_catalogue_featured_courses',
            { page },
            preSets,
        );
    }

    /**
     * Check if tool_catalogue_get_catalogue_featured_courses WS is available.
     *
     * @param siteId Site ID. If not defined, use current site.
     * @returns Whether the webservice is available.
     * @since 4.5
     */
     async isGetCatalogueFeaturedCoursesAvailable(siteId?: string): Promise<boolean> {
        const site = await CoreSites.getSite(siteId);

        return site?.wsAvailable('tool_catalogue_get_catalogue_featured_courses');
    }

    /**
     * Invalidates get featured courses WS call.
     *
     * @param categoryId Category ID.
     * @param siteId Site Id. If not defined, use current site.
     * @returns Promise resolved when the data is invalidated.
     */
    async invalidateFeaturedCourses(categoryId: number = 0, siteId?: string): Promise<void> {
        const site = await CoreSites.getSite(siteId);

        await site.invalidateWsCacheForKey(this.getFeaturedCoursesCacheKey(categoryId));
    }

    /**
     * Invalidates get catalogue page WS call.
     *
     * @param siteId Site Id. If not defined, use current site.
     * @returns Promise resolved when the data is invalidated.
     */
    async invalidateCataloguePage(siteId?: string): Promise<void> {
        const site = await CoreSites.getSite(siteId);

        await site.invalidateWsCacheForKeyStartingWith(this.getCatalogueCoursePrefixCacheKey());
    }

    /**
     * Get cache key for featured courses WS call.
     *
     * @param page Page.
     * @returns Cache key.
     */
    protected getFeaturedCoursesCacheKey(page: number): string {
        return WorkplaceCatalogueService.ROOT_CACHE_KEY + 'featuredcourses:' + page;
    }

    /**
     * Get cache key for catalogue page WS call.
     *
     * @param query Query.
     * @param filters Additional filters.
     * @returns Cache key.
     */
    protected getCataloguePageCacheKey(query: WorkplaceToolCatalogueGetCatalogueQuery, filters?: string): string {
        return this.getCatalogueCoursePrefixCacheKey() + this.getCataloguePageQuery(query, filters);
    }

    /**
     * Get catalogue course cache key prefix.
     *
     * @returns cache key prefix.
     */
    protected getCatalogueCoursePrefixCacheKey(): string {
        return WorkplaceCatalogueService.ROOT_CACHE_KEY + 'cataloguepage:';
    }

    /**
     * Get catalogue page query string.
     *
     * @param queryParams Category ID and page.
     * @param filters Additional filters.
     * @returns Retrieves catalogue page query string.
     */
    protected getCataloguePageQuery(queryParams: WorkplaceToolCatalogueGetCatalogueQuery, filters?: string): string {
        const queryParts: string[] = [];

        queryParams.page = queryParams.page ?? 0;

        for (const key in queryParams) {
            queryParts.push(`${key}=${queryParams[key]}`);
        }

        if (filters) {
            queryParts.push(filters);
        }

        return queryParts.join('&');
    }

}

export const WorkplaceCatalogue = makeSingleton(WorkplaceCatalogueService);

/**
 * Get catalogue page query.
 */
export type WorkplaceToolCatalogueGetCatalogueQuery = {
    page?: number;
    categoryid?: number;
    q?: string;
};

/**
 * Params of tool_catalogue_get_catalogue_page WS.
 *
 * WS Description: Get catalogue page
 */
export type WorkplaceToolCatalogueGetCataloguePageWSParams = {
    query: string; // URL query.
    isfrontpage?: boolean; // Is requested from the front page.
    returnfilters?: boolean; // Return available filters.
};

/**
 * Data returned by tool_catalogue_get_catalogue_page WS.
 *
 * WS Description: Get catalogue page
 */
export type WorkplaceToolCatalogueGetCataloguePageWSResponse = {
    contents?: {
        islistview: boolean; // Islistview.
        istilesview: boolean; // Istilesview.
        hasprices: boolean; // Hasprices.
        catalogueitems: WorkplaceCatalogueGetCataloguePageCatalogueItem[]; // Catalogue items
    };
    pagination?: WorkplaceCatalogueGetCatalogueCoursesPagination;
    url: string; // Respective page URL.
    heading?: string; // Search results heading.
    filters?: WorkplaceCatalogueFilter; // Filters.
};

/**
 * Catalogue page item.
 */
export type WorkplaceCatalogueGetCataloguePageCatalogueItem = {
    id: number; // Id.
    title: string; // Title.
    image: string; // Image.
    url: string; // Url.
    fields: WorkplaceToolCatalogueGetCataloguePageCatalogueItemField[];
};

/**
 * Catalogue page item field.
 */
export type WorkplaceToolCatalogueGetCataloguePageCatalogueItemField = {
    fieldname: string; // Fieldname.
    content: string; // Content.
    label: string; // Label.
    iscustomfield: boolean; // Iscustomfield.
    customfieldtype?: string; // Customfieldtype.
    isfeatured?: boolean; // Isfeatured.
    fieldvalue?: number; // Fieldvalue.
    isempty: boolean; // Isempty.
    showlabel: boolean; // Showlabel.
};

/**
 * Params of tool_catalogue_get_catalogue_featured_courses WS.
 *
 * WS Description: Get catalogue featured courses
 */
export type WorkplaceCatalogueGetCatalogueFeaturedCoursesWSParams = {
    page: number; // Pagination current page.
    returnpagination?: boolean; // Whether to return pagination.
};

/**
 * Data returned by tool_catalogue_get_catalogue_featured_courses WS.
 *
 * WS Description: Get catalogue featured courses
 */
export type WorkplaceCatalogueGetCatalogueFeaturedCoursesWSResponse = {
    contents?: {
        islistview: boolean; // Islistview.
        istilesview: boolean; // Istilesview.
        hasprices: boolean; // Hasprices.
        catalogueitems: WorkplaceCatalogueGetCataloguePageCatalogueItem[];
    };
    pagesize: number; // Courses page size.
    pagination?: WorkplaceCatalogueGetCatalogueCoursesPagination;
};

/**
 * Catalogue courses pagination.
 */
export type WorkplaceCatalogueGetCatalogueCoursesPagination = {
    previous?: {
        url: string; // Url.
        page?: number; // Page.
    };
    links: { // Links.
        url: string; // Url.
        page?: number; // Page.
        active?: boolean; // Active.
    }[];
    next?: {
        url: string; // Url.
        page?: number; // Page.
    };
    haspagination: boolean; // Haspagination.
};

export type WorkplaceCourseCatalogueItem = {
    id: number; // Id.
    title: string; // Title.
    url: string; // Url.
    fields: WorkplaceToolCatalogueGetCataloguePageCatalogueItemField[];

    dataCourseImage?: boolean;
    colorNumber?: number; // Color index number.
    color?: string; // Color RGB.
    courseimage?: string; // Course thumbnail.
};

export type WorkplaceCatalogueFilter = {
    url: string; // Url.
    filterhiddenfields: { // Filterhiddenfields.
        name: string; // Name.
        value: string; // Value.
    }[];
    hasfilters: boolean; // Hasfilters.
    filters: WorkplaceCatalogueFilterField[]; // Filters.
    checkedcount: number; // Checkedcount.
};

export type WorkplaceCatalogueFilterField = {
    label: string; // Label.
    fieldname: string; // Fieldname.
    hasadvanced: boolean; // Hasadvanced.
    options: { // Options.
        value: string; // Value.
        text: string; // Text.
        advanced: boolean; // Advanced.
        checked: boolean; // Checked.
    }[];
};
